import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { isAirgappedMode } from '../../utils/env';
import { isAuthenticatedPage } from '../../utils/url';
import { PlatformClient } from '../httpClients/PlatformClient';
import { IsFeatureEnabledResponse } from '../httpClients/PlatformClient/api.schema';
import { QUERY_KEYS } from './consts';

type OmittedQueryOptions = Omit<
  UseQueryOptions<IsFeatureEnabledResponse>,
  'queryKey' | 'queryFn'
>;

// Mirrors the QueryClient default `retry: 3` (queryClient.ts) so 5xx / network
// blips keep their existing retry budget; 4xx is short-circuited below.
const RETRY_LIMIT = 3;

/**
 * A 4xx from the feature service is a definitive answer, not a transient
 * failure: 404 = the flag isn't registered, 403 = not granted. Retrying can't
 * change it, so a 404 flag (e.g. `research_preview` on a tenant where it isn't
 * provisioned) shouldn't fan out into 4 identical requests + 4 console errors
 * (PER-139). Treat 4xx as terminal — EXCEPT 429 (Too Many Requests), which IS
 * transient and is exactly the case a retry (with backoff) is meant to ride
 * out. For everything else (5xx, network blips, 429) fall back to the SAME
 * retry budget the global QueryClient default would have applied — including
 * `false` under test mode — so this only suppresses the genuine 4xx storm and
 * doesn't silently broaden retry behavior for every feature flag (the hook is
 * shared by useFeatureAccess + the integration flags in useUser).
 */
const shouldRetryFeatureQuery = (
  failureCount: number,
  error: unknown,
): boolean => {
  const status = isAxiosError(error) ? error.response?.status : undefined;

  const isTerminalClientError =
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    status !== 429; /* 429 is transient — keep retrying */

  if (isTerminalClientError) return false;

  // Preserve the global default's test-mode no-retry so feature-flag queries
  // in tests don't start fanning out on mocked 5xx/network errors.
  if (import.meta.env.MODE === 'test') return false;

  return failureCount < RETRY_LIMIT;
};

export const useIsFeatureEnabledQuery = (
  featureId: string,
  queryOptions: OmittedQueryOptions = {},
) =>
  useQuery({
    queryKey: [QUERY_KEYS.FEATURE_ENABLED, featureId],
    queryFn: () => PlatformClient.isFeatureEnabled(featureId),
    select: (data): IsFeatureEnabledResponse => data,
    staleTime: 3 * 60 * 1000,
    retry: shouldRetryFeatureQuery,
    ...queryOptions,
    // Caller-supplied `enabled` is ANDed with the base guard, never replaces
    // it — the auth/airgap/feature-id conditions must hold for every caller.
    enabled:
      isAuthenticatedPage() &&
      !isAirgappedMode() &&
      !!featureId &&
      (queryOptions.enabled ?? true),
  });
